// ExternalRecipientsDrawer.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";

import { GBH_ID, SanitizeHotelID, URLAPI } from "../configuration";
import Icons from "../helpers/icons";

const drawerWidth = 600;

const ExternalRecipientsDrawer = ({
    open,
    onClose,
    hotelID,
    auth,
    onSaved,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [fullNameError, setFullNameError] = useState(null);

    const [noticeSeverity, setNoticeSeverity] = useState("warning");
    const [notice, setNotice] = useState(null);

    const [assigned, setAssigned] = useState([]);

    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [recipientToRemove, setRecipientToRemove] = useState(null);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${auth.user.jwt}` }),
        [auth],
    );

    const fetchAssigned = useCallback(async () => {
        if (!hotelID || !open) return;
        setLoading(true);
        // setNotice(null);
        try {
            const hid = SanitizeHotelID(hotelID, GBH_ID);

            const res = await Axios.get(
                `${URLAPI}/hotels/externals/assignments`,
                {
                    params: { hotel_id: hid, only_enabled: false },
                    headers,
                },
            );

            const rows = res?.data?.data ?? [];

            const map = new Map();
            rows.forEach((r) => {
                if (!map.has(r.RecipientID)) {
                    const name =
                        r.FirstName || r.LastName
                            ? `${r.FirstName ?? ""} ${r.LastName ?? ""}`.trim()
                            : null;
                    map.set(r.RecipientID, {
                        RecipientID: r.RecipientID,
                        Email: r.Email,
                        Name: name,
                        RecipientEnabled: !!r.RecipientEnabled,
                        reportTypes: [],
                    });
                }
                map.get(r.RecipientID).reportTypes.push(r.ReportType);
            });

            setAssigned(
                Array.from(map.values()).sort((a, b) =>
                    String(a.Email || "").localeCompare(String(b.Email || "")),
                ),
            );
        } catch (err) {
            setNoticeSeverity("error");
            setNotice("Could not load external contacts. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [hotelID, open, headers, URLAPI]);

    useEffect(() => {
        fetchAssigned();
    }, [fetchAssigned]);

    const handleRemoveFromHotel = (recipient) => {
        if (!hotelID) return;
        setRecipientToRemove(recipient);
        setConfirmOpen(true);
    };

    const handleConfirmRemove = async () => {
        if (!hotelID || !recipientToRemove) return;

        setConfirmOpen(false);
        setSaving(true);
        setNotice(null);
        const recipient = recipientToRemove;

        try {
            const hid = SanitizeHotelID(hotelID, GBH_ID);

            const res = await Axios.get(
                `${URLAPI}/hotels/externals/assignments`,
                {
                    params: {
                        hotel_id: hid,
                        recipient_id: recipient.RecipientID,
                        only_enabled: false,
                    },
                    headers,
                },
            );

            const rows = res?.data?.data ?? [];
            const types =
                Array.from(new Set(rows.map((r) => r.ReportType))) ||
                (recipient.reportTypes?.length
                    ? recipient.reportTypes
                    : ["LastMonthSummaryTable"]);

            await Promise.all(
                types.map((rt) =>
                    Axios.delete(`${URLAPI}/hotels/externals/assignments`, {
                        params: {
                            recipient_id: recipient.RecipientID,
                            hotel_id: hid,
                            report_type: rt,
                        },
                        headers,
                    }),
                ),
            );

            await fetchAssigned();
            onSaved?.();
            setNoticeSeverity("success");
            setNotice("Recipient removed from this hotel.");
        } catch (err) {
            setNoticeSeverity("error");
            setNotice("Could not remove the recipient. Please try again.");
        } finally {
            setSaving(false);
            setRecipientToRemove(null);
        }
    };

    const handleCancelRemove = () => {
        if (saving) return;
        setConfirmOpen(false);
        setRecipientToRemove(null);
    };

    const handleCreateAndAssign = async () => {
        if (!hotelID) return;

        const fullNameTrim = (fullName || "").trim();
        if (!fullNameTrim) {
            setFullNameError("Full name is required.");
            setNotice("Please enter a full name before saving.");
            setNoticeSeverity("error");
            return;
        }

        const emailTrim = (email || "").trim().toLowerCase();
        if (!emailTrim) {
            setEmailError("Email is required.");
            setNotice("Please enter an email before saving.");
            setNoticeSeverity("error");
            return;
        }

        setSaving(true);
        setNotice(null);

        try {
            const hid = SanitizeHotelID(hotelID, GBH_ID);

            const parts = (fullNameTrim || "").trim().split(/\s+/);
            const first_name = parts[0] || null;
            const last_name =
                parts.length > 1 ? parts.slice(1).join(" ") : null;

            const upsertRes = await Axios.post(
                `${URLAPI}/hotels/externals`,
                {
                    email_address: emailTrim,
                    first_name,
                    last_name,
                    enabled: "True",
                },
                { headers },
            );

            await Axios.post(
                `${URLAPI}/hotels/externals/assignments`,
                {
                    email_address: emailTrim,
                    hotel_id: hid,
                    report_type: "LastMonthSummaryTable",
                    enabled: "True",
                },
                { headers },
            );

            if (upsertRes?.status === 200 && (fullNameTrim || "").trim()) {
                // comment: user already existed; name update applies across all hotels
                setNotice(
                    "User added to this hotel. Since this user already existed, the updated name has been applied across all hotels.",
                );
                setNoticeSeverity("warning");
            } else {
                // comment: brand new recipient
                setNoticeSeverity("success");
                setNotice("Recipient created and assigned successfully.");
            }

            setEmail("");
            setFullName("");
            setEmailError(null);
            setFullNameError(null);
            await fetchAssigned();
            onSaved?.();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";

            if (/invalid email/i.test(msg)) {
                setEmailError("Invalid email format.");
                setNoticeSeverity("error");
                setNotice("Please correct the email and try again.");
            } else {
                setNoticeSeverity("error");
                setNotice("Could not save this recipient. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleRequestClose = () => {
        if (saving) return;
        onClose();
    };

    return (
        <Drawer anchor="right" open={open} onClose={handleRequestClose}>
            <Box
                sx={{
                    p: 6,
                    width: drawerWidth,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h5">Edit external contacts</Typography>
                    <IconButton onClick={handleRequestClose}>
                        <Icons.CloseNavbar />
                    </IconButton>
                </Stack>
                <Divider sx={{ my: 2 }} />

                {/* Content */}
                <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                my: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {notice && (
                                <Alert
                                    severity={noticeSeverity}
                                    sx={{ mb: 2 }}
                                    onClose={() => setNotice(null)}
                                >
                                    {notice}
                                </Alert>
                            )}

                            {/* Assigned to this hotel */}
                            <Typography fontSize="0.8em">
                                External contacts
                            </Typography>
                            <List sx={{ ml: 1, mr: 1 }}>
                                {assigned.length === 0 && (
                                    <Typography
                                        sx={{ ml: 2, my: 1 }}
                                        fontSize="0.8em"
                                    >
                                        No external recipients assigned.
                                    </Typography>
                                )}
                                {assigned.map((r) => (
                                    <ListItem
                                        key={r.RecipientID}
                                        sx={{
                                            border: 1,
                                            borderColor: "grey.400",
                                            borderRadius: 1,
                                            mb: 1,
                                        }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    handleRemoveFromHotel(r)
                                                }
                                                disabled={saving}
                                            >
                                                <Icons.Remove />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={r.Name || "—"}
                                            secondary={
                                                r.Email
                                                // +  (r.reportTypes?.length
                                                //     ? `  ·  ${r.reportTypes.join(
                                                //           ", "
                                                //       )}`
                                                //     : "")
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Divider sx={{ my: 2 }} />

                            {/* Create / assign new external */}
                            <Typography fontSize="0.8em" sx={{ mb: 1 }}>
                                Add new external contact here.{" "}
                            </Typography>
                            <TextField
                                fullWidth
                                label="Full name"
                                placeholder="First Last"
                                error={!!fullNameError}
                                helperText={fullNameError || " "}
                                sx={{ pb: !!fullNameError ? 2 : 0 }}
                                value={fullName}
                                onChange={(e) => {
                                    setFullName(e.target.value);
                                    if (fullNameError) setFullNameError(null);
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                placeholder="email@example.com"
                                value={email}
                                error={!!emailError}
                                helperText={emailError || " "}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (emailError) setEmailError(null);
                                }}
                                sx={{ pb: !!emailError ? 2 : 0 }}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    disabled={saving}
                                    sx={{ textTransform: "none" }}
                                    onClick={handleCreateAndAssign}
                                >
                                    {saving ? "Saving…" : "Create contact"}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            <Dialog
                open={confirmOpen}
                onClose={handleCancelRemove}
                aria-labelledby="remove-external-recipient-title"
            >
                <DialogTitle
                    id="remove-external-recipient-title"
                    sx={{ maxWidth: 500 }}
                >
                    Remove
                    <b>{` ${recipientToRemove?.Email ?? ""} `}</b>
                    from this hotel?
                </DialogTitle>

                <DialogContent sx={{ maxWidth: 500 }}>
                    <DialogContentText>
                        This will delete all their report assignments for this
                        hotel.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCancelRemove} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmRemove}
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? "Removing…" : "Remove"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};

export default ExternalRecipientsDrawer;
